import React, { useEffect, useState } from "react";
import RolesTable from "./RolesTable";
import CreateRoleModal from "./CreateRoleModal";
import PermissionsModal from "./PermissionsModal";
import apis from "../../services/apis";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import apiUrls from "../../constants/apiUrls";
import apiCommonParams from "../../constants/apiCommonParams";
import { customFormDataDelete } from "../../helper/api-helper/apiHerlpers";
import { useSmarkState } from "../../Context/KeycloakContext";

const RolesList = ({ applicationId, siteId, moduleId }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const userDetails = useSmarkState();
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [permissionList, setPermissionList] = useState([]);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);

  const storedToken = localStorage.getItem("keycloakToken");

  /* ========= LOAD ROLES ========= */
  const loadRoles = async () => {
    setLoading(true);
    try {
      const rolesList = await apis.getRolesList(
        { applicationId, siteId, moduleId },
        storedToken
      );
      setRoles(sortRoles(rolesList || []));
    } catch (err) {
      console.error("Failed to load roles", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [applicationId, siteId, moduleId]);

  /* ========= CREATE / EDIT ========= */
  const handleCreate = () => {
    setEditingRole(null);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setShowModal(true);
  };

  const handleRoleSaved = (role) => {
    if (!role) return;

    setRoles((prev) => {
      const index = prev.findIndex((r) => r.roleId === role.roleId);
      let updated;

      if (index >= 0) {
        updated = [...prev];
        updated[index] = role;
      } else {
        updated = [role, ...prev];
      }

      return sortRoles(updated);
    });
  };

  /* ========= DELETE ROLE ========= */
  const roleDeleteConfirm = (role) => {
    confirmDialog({
      message: "Are you sure you want to delete this role?",
      header: "Delete Confirmation",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => deleteRoleById(role),
    });
  };

  const deleteRoleById = async (role) => {
    const previousRoles = [...roles];
    setRoles((prev) => prev.filter((r) => r.roleId !== role.roleId));

    try {
      const formData = new FormData();
      formData.append("applicationId", applicationId);
      formData.append("siteId", siteId);
      formData.append("roleId", role.roleId);
      formData.append("performedByUserId", userDetails.userId);
      formData.append("swcmTicket", apiCommonParams.swcmTicket);

      const result = await customFormDataDelete(
        apiUrls.deleteRole,
        formData,
        storedToken
      );

      if (result?.code === "SUCCESS") {
        toast.success("Role deleted successfully");
      } else {
        setRoles(previousRoles);
        toast.error(result?.message || "Delete failed");
      }
    } catch (err) {
      setRoles(previousRoles);
      toast.error("Something went wrong while deleting role");
    }
  };

  /* ========= PERMISSIONS ========= */
  const handlePermissions = async (role) => {
    setSelectedRole(role);

    try {
      const currentModuleId = moduleId || 0; // site = 0

      const [allRes, assignedRes] = await Promise.all([
        apis.getRolePermissions(
          applicationId,
          siteId,
          currentModuleId,
          storedToken
        ),
        apis.getAssignedPermissions(
          applicationId,
          siteId,
          role.roleId,
          storedToken
        ),
      ]);

      const allPermissions =
        JSON.parse(allRes.json)?.permissionTOList || [];

      const assignedPermissions =
        JSON.parse(assignedRes.json)?.permissionTOList || [];

      setPermissionList(allPermissions);
      setAssignedPermissionIds(
        assignedPermissions.map((p) => p.permissionId)
      );

      setShowPermissions(true);
    } catch (err) {
      console.error("Permission load failed", err);
      toast.error("Failed to load permissions");
    }
  };

  /* ========= SORT ========= */
  const sortRoles = (rolesList) =>
    [...rolesList].sort((a, b) => a.roleLevel - b.roleLevel);

  return (
    <>
      <ConfirmDialog />

      <RolesTable
        roles={roles}
        loading={loading}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={roleDeleteConfirm}
        onPermissions={handlePermissions}
      />

      {showModal && (
        <CreateRoleModal
          show={showModal}
          onHide={() => setShowModal(false)}
          applicationId={applicationId}
          siteId={siteId}
          moduleId={moduleId}
          roleToEdit={editingRole}
          onSuccess={handleRoleSaved}
        />
      )}

      {showPermissions && (
        <PermissionsModal
          show={showPermissions}
          onHide={() => setShowPermissions(false)}
          role={selectedRole}
          permissions={permissionList}
          assignedPermissionIds={assignedPermissionIds}
          applicationId={applicationId}
          siteId={siteId}
          moduleId={moduleId}
        />
      )}
    </>
  );
};

export default RolesList;
